import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhotoHero, Reveal, Img, Container, SectionTitle, HScroll } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

export default function Dining() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const d = data?.dining;
  usePageMeta(tx("Mat", "Dining"), tx("Restaurant ved Fitjarvika. Komler på torsdagar, familiebuffé på søndagar og mat til selskap.", "Restaurant on Fitjarvika. Komle on Thursdays, family buffet on Sundays and food for parties."));
  const offers = d?.offers || [];
  const askTable = () => ask(lang === "en" ? "I would like to book a table. Which day and time are possible?" : "Eg vil gjerne bestille bord. Kva dag og tid er mogleg?");

  const gallery = [
    { src: "/images/food-mussels.jpg", caption: tx("Blåskjel frå kjøkenet", "Mussels from the kitchen"), aspect: "aspect-[4/5]" },
    { src: "/images/terrace-table.jpg", caption: tx("Bord på terrassen", "A table on the terrace"), aspect: "aspect-[4/3]", w: "w-[84vw] sm:w-[56vw] lg:w-[520px]" },
    { src: "/images/food-potato.jpg", caption: tx("Poteter frå Fitjar", "Potatoes from Fitjar"), aspect: "aspect-[4/5]" },
    { src: "/images/restaurant-table.jpg", caption: tx("Dekka bord ved vindauget", "A set table by the window"), aspect: "aspect-[4/5]" },
    { src: "/images/terrace-still-life.jpg", caption: tx("Kaffi på terrassen", "Coffee on the terrace"), aspect: "aspect-[4/5]" },
    { src: "/images/sunset-terrace.jpg", caption: tx("Kveld i restauranten", "Evening at the restaurant"), aspect: "aspect-[4/5]" },
  ];

  return (
    <div data-testid="dining-page">
      <PhotoHero
        testid="dining-hero"
        image="/images/terrace-table.jpg"
        position="50% 60%"
        alt={tx("Terrassen ved restauranten", "The terrace by the restaurant")}
        kicker={tx("Restauranten ved Fitjarvika", "The restaurant on Fitjarvika")}
        title={tx("Velkomen til bords", "Welcome to the table")}
        lead={tx("Kom innom til middag i restauranten ved Fitjarvika. På torsdagar serverer me komler, og på søndagar er det familiebuffé med dessertbord.", "Join us for dinner in the restaurant on Fitjarvika. On Thursdays we serve komle, and on Sundays there is a family buffet with a dessert table.")}
      >
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <Button size="lg" variant="inverse" onClick={askTable} data-testid="dining-book-table"><MessageCircle className="h-4 w-4" /> {tx("Bestill bord", "Book a table")}</Button>
          <p className="hero-shadow text-sm text-white/75">{tx("Du sender ein førespurnad – me stadfestar bordet på e-post eller telefon.", "You send a request – we confirm the table by email or phone.")}</p>
        </div>
      </PhotoHero>

      {/* Offers */}
      <section className="py-20 lg:py-32" data-testid="dining-offers">
        <Container>
          <SectionTitle size="lg" title={tx("Faste dagar på menyen", "Regular days on the menu")} />
          <div className="mt-10 grid gap-x-7 gap-y-12 sm:grid-cols-2">
            {offers.map((o, i) => (
              <Reveal key={o.id} delay={(i % 2) * 0.05}>
                <article className="group" data-testid={`dining-offer-${o.id}`}>
                  <Img src={o.image} alt={tr(o.title)} className="img-zoom aspect-[16/10]" radius="rounded-[22px]" sizes={SIZES.half} />
                  <p className="mt-5 text-sm font-medium text-muted-foreground">{tr(o.when)}</p>
                  <h3 className="mt-1.5 text-[1.375rem] leading-tight tracking-[-0.02em] text-foreground sm:text-2xl">{tr(o.title)}</h3>
                  <p className="mt-3 max-w-[56ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(o.text)}</p>
                  {o.id === "party" && (
                    <Button asChild variant="soft" className="mt-5" data-testid="dining-party-cta"><Link to="/arrangement?type=celebration#forespurnad">{tx("Spør om selskapsmeny", "Ask about the party menu")} <ArrowRight className="h-4 w-4" /></Link></Button>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Hours */}
      <section className="bg-secondary/70 py-20 lg:py-32" data-testid="dining-hours">
        <Container className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionTitle size="lg" title={tx("Opningstider", "Opening hours")} />
            <p className="mt-4 max-w-[48ch] text-base leading-relaxed text-muted-foreground">{tr(d?.hours_note)}</p>
            <Button variant="soft" size="lg" className="mt-7" onClick={askTable} data-testid="dining-ask-table"><MessageCircle className="h-4 w-4" /> {tx("Bestill bord", "Book a table")}</Button>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-7">
            <ul className="rounded-[24px] bg-card px-6 ring-hairline sm:px-8" data-testid="dining-hours-list">
              {(d?.hours || []).map((h) => (
                <li key={h.key} className="flex items-baseline justify-between gap-6 border-b border-border py-4 last:border-0" data-testid={`dining-hours-${h.key}`}>
                  <span className="text-[0.9375rem] text-foreground">{tr(h.label)}</span>
                  <span className="shrink-0 text-[0.9375rem] font-semibold tabular text-foreground">{h.value}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </Container>
      </section>

      {/* Catering */}
      <section className="py-20 lg:py-32" data-testid="dining-catering">
        <Container className="grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-16">
          <Reveal className="lg:col-span-6">
            <Img src="/images/food-mussels.jpg" alt={tx("Blåskjel frå kjøkenet", "Mussels from the kitchen")} className="img-zoom aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]" radius="rounded-[24px] sm:rounded-[28px]" sizes={SIZES.half} />
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-6">
            <h2 className="text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem] lg:text-[2.75rem]">{tx("Mat til selskapet heime", "Food for your party at home")}</h2>
            <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tr(d?.catering_intro)}</p>
            <ul className="mt-8 divide-y divide-border border-y border-border" data-testid="dining-catering-list">
              {(d?.catering || []).map((c) => (
                <li key={c.id} className="py-3.5 text-[1.0625rem] font-medium text-foreground" data-testid={`dining-catering-${c.id}`}>{tr(c.title)}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Button asChild size="lg" data-testid="dining-catering-cta"><Link to="/arrangement?type=celebration#forespurnad">{tx("Spør om catering", "Ask about catering")} <ArrowRight className="h-4 w-4" /></Link></Button>
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Truck className="h-4 w-4" /> {tx("Levering i Sunnhordland etter avtale", "Delivery in Sunnhordland by arrangement")}</span>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Gallery */}
      <section className="pb-20 lg:pb-32" data-testid="dining-gallery-section">
        <Container>
          <SectionTitle size="lg" title={tx("Frå restauranten", "From the restaurant")} action={tx("Bestill rom", "Book a room")} to="/bestill" testid="dining-gallery-title" />
          <Reveal className="mt-10"><HScroll items={gallery} testid="dining-gallery" /></Reveal>
        </Container>
      </section>
    </div>
  );
}
